import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  Settings as SettingsIcon, 
  User, 
  Moon, 
  Globe, 
  Bell, 
  Lock, 
  Shield, 
  HelpCircle, 
  LogOut, 
  Trash2,
  ChevronRight,
  Edit
} from 'lucide-react';
import { motion } from 'framer-motion';
import BottomNavigation from './BottomNavigation';
import toast from 'react-hot-toast';

const Settings = () => {
  const navigate = useNavigate();
  const { 
    language, 
    setLanguage, 
    theme, 
    setTheme, 
    logout,
    user,
    addActivity
  } = useApp();

  const translations = {
    english: {
      settings: 'Settings',
      manageAccount: 'Manage your account and preferences',
      profile: 'Profile',
      myProfile: 'My Profile',
      appearance: 'Appearance',
      darkMode: 'Dark Mode',
      language: 'Language',
      notifications: 'Notifications',
      pushNotifications: 'Push Notifications',
      security: 'Security',
      changePassword: 'Change Password',
      privacySettings: 'Privacy Settings',
      support: 'Support',
      helpSupport: 'Help & Support',
      account: 'Account',
      logout: 'Logout',
      deleteAccount: 'Delete Account',
      version: 'Version 1.0.0',
      english: 'English',
      hindi: 'हिंदी (Hindi)',
      logoutSuccess: 'Logged out successfully',
      accountDeleted: 'Account deleted successfully',
      logoutConfirm: 'Are you sure you want to logout?',
      deleteConfirm: 'Are you sure you want to delete your account? This action cannot be undone.'
    },
    hindi: {
      settings: 'सेटिंग्स',
      manageAccount: 'अपने खाते और प्राथमिकताओं का प्रबंधन करें',
      profile: 'प्रोफ़ाइल',
      myProfile: 'मेरी प्रोफ़ाइल',
      appearance: 'दिखावट',
      darkMode: 'डार्क मोड',
      language: 'भाषा',
      notifications: 'सूचनाएं',
      pushNotifications: 'पुश सूचनाएं',
      security: 'सुरक्षा',
      changePassword: 'पासवर्ड बदलें',
      privacySettings: 'गोपनीयता सेटिंग्स',
      support: 'सहायता',
      helpSupport: 'मदद और सहायता',
      account: 'खाता',
      logout: 'लॉगआउट',
      deleteAccount: 'खाता हटाएं',
      version: 'संस्करण 1.0.0',
      english: 'English',
      hindi: 'हिंदी (Hindi)',
      logoutSuccess: 'सफलतापूर्वक लॉगआउट हो गया',
      accountDeleted: 'खाता सफलतापूर्वक हटा दिया गया',
      logoutConfirm: 'क्या आप वाकई लॉगआउट करना चाहते हैं?',
      deleteConfirm: 'क्या आप वाकई अपना खाता हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।'
    }
  };

  const t = translations[language] || translations.english;

  const handleLogout = () => {
    if (window.confirm(t.logoutConfirm)) {
      // Add activity for logout
      addActivity({
        type: 'logout',
        title: 'User logged out',
        description: 'User logged out from the application',
        icon: 'alert',
        priority: 'medium'
      });
      
      logout();
      toast.success(t.logoutSuccess);
      // Note: logout() function in AppContext already handles navigation to /auth
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm(t.deleteConfirm)) {
      // Add activity for account deletion
      addActivity({
        type: 'account_deleted',
        title: 'Account deleted',
        description: 'User account was permanently deleted',
        icon: 'alert',
        priority: 'high'
      });
      
      logout();
      toast.success(t.accountDeleted);
      // Note: logout() function in AppContext already handles navigation to /auth
    }
  };

  const settingsSections = [
    {
      title: t.profile,
      items: [
        {
          icon: User,
          label: t.myProfile,
          action: () => navigate('/profile'),
          rightIcon: Edit
        }
      ]
    },
    {
      title: t.appearance,
      items: [
        {
          icon: Moon,
          label: t.darkMode,
          action: () => setTheme(theme === 'light' ? 'dark' : 'light'),
          toggle: true,
          value: theme === 'dark'
        },
        {
          icon: Globe,
          label: t.language,
          action: () => setLanguage(language === 'english' ? 'hindi' : 'english'),
          value: language === 'english' ? t.english : t.hindi
        }
      ]
    },
    {
      title: t.notifications,
      items: [
        {
          icon: Bell,
          label: t.pushNotifications,
          action: () => {},
          toggle: true,
          value: true
        }
      ]
    },
    {
      title: t.security,
      items: [
        {
          icon: Lock,
          label: t.changePassword,
          action: () => toast.success('Change password feature coming soon!')
        },
        {
          icon: Shield,
          label: t.privacySettings,
          action: () => toast.success('Privacy settings feature coming soon!')
        }
      ]
    },
    {
      title: t.support,
      items: [
        {
          icon: HelpCircle,
          label: t.helpSupport,
          action: () => toast.success('Help & Support feature coming soon!')
        }
      ]
    },
    {
      title: t.account,
      items: [
        {
          icon: LogOut,
          label: t.logout,
          action: handleLogout,
          danger: true
        },
        {
          icon: Trash2,
          label: t.deleteAccount,
          action: handleDeleteAccount,
          danger: true
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t.settings}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t.manageAccount}
        </p>
      </div>

      <div className="px-6 py-6 space-y-6">
        {settingsSections.map((section, sectionIndex) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {section.title}
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {section.items.map((item, itemIndex) => (
                <div
                  key={item.label}
                  className={`flex items-center justify-between p-4 ${
                    itemIndex !== section.items.length - 1
                      ? 'border-b border-gray-200 dark:border-gray-700'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      item.danger 
                        ? 'bg-red-100 dark:bg-red-900/20' 
                        : 'bg-purple-100 dark:bg-purple-900/20'
                    }`}>
                      <item.icon className={`w-5 h-5 ${
                        item.danger 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-purple-600 dark:text-purple-400'
                      }`} />
                    </div>
                    <span className={`font-medium ${
                      item.danger 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {item.value && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {item.value}
                      </span>
                    )}
                    
                    {item.toggle ? (
                      <button
                        onClick={item.action}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          item.value ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                            item.value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    ) : (
                      <button
                        onClick={item.action}
                        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                      >
                        {item.rightIcon ? (
                          <item.rightIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}

        {/* Version Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center py-4"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            NyaySahaya {t.version}
          </p>
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation activePage="settings" />
    </div>
  );
};

export default Settings;